import { FlexBox, Icon, Typography } from '@vp/swan';
import type { ReactNode } from 'react';
import { ColorSelector } from '../components/ColorSelector';
import { ConfirmationCard } from '../components/ConfirmationCard';
import { ProductOptionsSelector } from '../components/ProductOptionsSelector';
import { ReferenceProjectsSelector } from '../components/ReferenceProjects';
import { ReviewMethodSelector } from '../components/ReviewMethodSelector';
import { StyleTilesSelector } from '../components/StyleTilesSelector';
import { TileCard } from '../components/TileCard';
import { TileChoiceSelector } from '../components/TileChoiceSelector';
import {
    PRODUCT_OPTIONS_CHOICE_CUSTOMER,
    PRODUCT_OPTIONS_CHOICE_DESIGNER,
    REFERENCE_PROJECTS_DECISION_WITH,
    REFERENCE_PROJECTS_DECISION_WITHOUT,
    REVIEW_METHOD,
} from '../types/selectors';
import { formatVideoConfirmation, reconstructVideoConfirmation } from '../utils/videoConfirmation';
import type { PartialDesignBrief } from '../types/briefSchema';
import type { Message, ParsedComponent, ParsedComponentType } from '../types/chat';
import type { ReviewMethod } from '../types/selectors';
import type {
    ColorOption,
    ProductOptionsSelection,
    ReferenceProjectsData,
    ReferenceProjectsResult,
    ReviewMethodData,
    ReviewMethodOption,
    ReviewMethodSelection,
    StyleOption,
    TileChoiceOption,
} from '../types/selectors';

/* ─── Result types ──────────────────────────────────────────────────────── */

/**
 * Map from selector type → the *value* the user produces when completing it.
 * Adding a new selector means adding a new entry here, plus a new entry in
 * SELECTOR_REGISTRY below. Nothing else in the codebase should need a switch.
 */
export interface SelectorResultMap {
    'style-tiles': StyleOption;
    'color-selector': ColorOption[];
    'icon-tiles': TileChoiceOption;
    'review-method': ReviewMethodSelection;
    'product-options': ProductOptionsSelection;
    'reference-projects': ReferenceProjectsResult;
}

export type SelectorType = ParsedComponentType;
export type SelectorResult<T extends SelectorType> = SelectorResultMap[T];

/* ─── Descriptor types ──────────────────────────────────────────────────── */

export interface CompletionContext {
    /** The brief *as it stands before* the patch this descriptor returns is applied. */
    designBrief: PartialDesignBrief;
}

export interface CompletionOutcome {
    /** Message to append to the chat log on behalf of the user. */
    message: Message;
    /** Optional patch to apply to the design brief. */
    briefPatch?: Partial<PartialDesignBrief>;
    /**
     * If true, skip the Claude turn after appending (e.g. brief is complete and
     * there's nothing left for Claude to ask).
     */
    skipTurn?: boolean;
}

export interface RenderCallbacks<T extends SelectorType> {
    onComplete: (result: SelectorResult<T>) => void;
    designBrief: PartialDesignBrief;
}

export interface SelectorDescriptor<T extends SelectorType> {
    type: T;
    /** Regex used to detect this component in raw Claude output. */
    marker: RegExp;
    /**
     * Lower runs first. REVIEW_METHOD must precede ICON_TILES so its marker
     * isn't shadowed by a substring match.
     */
    markerPriority: number;
    /** Validate the JSON payload from inside the [COMPONENT:*] block. */
    validate(raw: unknown): Extract<ParsedComponent, { type: T }> | null;
    /** Render the selector input UI inside the chat input area. */
    render(
        data: Extract<ParsedComponent, { type: T }>['data'],
        callbacks: RenderCallbacks<T>
    ): ReactNode;
    /** Turn a user's selection into an outcome (message + brief patch + maybe skipTurn). */
    completionFor(result: SelectorResult<T>, ctx: CompletionContext): CompletionOutcome;
    /** Render a static message view for history display. Implemented per-component in Wave 3. */
    renderMessageView?: (data: unknown) => ReactNode;
}

/* ─── Shared validators ─────────────────────────────────────────────────── */

function isRecord(v: unknown): v is Record<string, unknown> {
    return typeof v === 'object' && v !== null;
}

function isStringArray(v: unknown): v is string[] {
    return Array.isArray(v) && v.every((x) => typeof x === 'string');
}

function isStringRecord(v: unknown): v is Record<string, string> {
    return isRecord(v) && Object.values(v).every((x) => typeof x === 'string');
}

function mapAll<T>(arr: unknown, fn: (x: unknown) => T | null): T[] | null {
    if (!Array.isArray(arr)) return null;
    const out: T[] = [];
    for (const item of arr) {
        const v = fn(item);
        if (v === null) return null;
        out.push(v);
    }
    return out;
}

function pickTitle(d: Record<string, unknown>): string | undefined {
    return typeof d.title === 'string' ? d.title : undefined;
}

/* ─── Per-option validators ─────────────────────────────────────────────── */

function validateStyleOption(o: unknown): StyleOption | null {
    if (!isRecord(o)) return null;
    const { value, name, description, imageUrl } = o;
    if (
        typeof value !== 'string' ||
        typeof name !== 'string' ||
        typeof description !== 'string' ||
        typeof imageUrl !== 'string'
    )
        return null;
    return { value, name, description, imageUrl };
}

function validateColorOption(o: unknown): ColorOption | null {
    if (!isRecord(o)) return null;
    const { value, name, imageUrl } = o;
    if (typeof value !== 'string' || typeof name !== 'string' || typeof imageUrl !== 'string')
        return null;
    return { value, name, imageUrl };
}

const TILE_CHOICE_ICONS = ['online', 'video', 'business', 'personal'] as const;

function validateTileChoiceOption(o: unknown): TileChoiceOption | null {
    if (!isRecord(o)) return null;
    const { value, name, icon } = o;
    if (
        typeof value !== 'string' ||
        typeof name !== 'string' ||
        typeof icon !== 'string' ||
        !TILE_CHOICE_ICONS.includes(icon as TileChoiceOption['icon'])
    )
        return null;
    return { value, name, icon: icon as TileChoiceOption['icon'] };
}

// Normalize defensively — LLM output drifts even when the prompt is updated.
// Accept legacy/lowercase tokens and map to the canonical PascalCase vocabulary.
function normalizeReviewMethod(v: unknown): ReviewMethod | null {
    if (v === 'Standard' || v === 'standard' || v === 'online') return REVIEW_METHOD.Standard;
    if (v === 'DesignLive' || v === 'designLive' || v === 'video') return REVIEW_METHOD.DesignLive;
    return null;
}

function validateReviewMethodOption(o: unknown): ReviewMethodOption | null {
    if (!isRecord(o)) return null;
    const { name } = o;
    if (typeof name !== 'string') return null;

    const value = normalizeReviewMethod(o.value);
    if (!value) return null;

    return { value, name };
}

/* ─── Descriptors ───────────────────────────────────────────────────────── */

const styleTilesDescriptor: SelectorDescriptor<'style-tiles'> = {
    type: 'style-tiles',
    marker: /\[COMPONENT:STYLE_TILES\]([\s\S]*?)\[\/COMPONENT\]/,
    markerPriority: 10,
    validate(raw) {
        if (!isRecord(raw)) return null;
        const options = mapAll(raw.options, validateStyleOption);
        if (!options) return null;
        const preselected = typeof raw.preselected === 'string' ? raw.preselected : undefined;
        return { type: 'style-tiles', data: { title: pickTitle(raw), options, preselected } };
    },
    render(data, { onComplete, designBrief }) {
        const briefFallback = data.options.find(
            (o) => o.name === designBrief.stylePreference
        )?.value;
        return (
            <StyleTilesSelector
                options={data.options}
                defaultValue={data.preselected ?? briefFallback}
                onComplete={onComplete}
            />
        );
    },
    completionFor(option) {
        return {
            message: {
                type: 'componentMessage',
                role: 'user',
                componentType: 'style-tiles',
                data: {
                    value: option.value,
                    name: option.name,
                    imageUrl: option.imageUrl,
                },
            },
            // No briefPatch — backend document response handles this
            skipTurn: false,
        };
    },
    renderMessageView(data: unknown) {
        const d = data as { value: string; name: string; imageUrl: string };
        return <TileCard imageUrl={d.imageUrl} name={d.name} imageHeight={80} />;
    },
};

const colorSelectorDescriptor: SelectorDescriptor<'color-selector'> = {
    type: 'color-selector',
    marker: /\[COMPONENT:COLOR_SELECTOR\]([\s\S]*?)\[\/COMPONENT\]/,
    markerPriority: 20,
    validate(raw) {
        if (!isRecord(raw)) return null;
        const options = mapAll(raw.options, validateColorOption);
        if (!options) return null;
        const preselected = isStringArray(raw.preselected) ? raw.preselected : undefined;
        return { type: 'color-selector', data: { title: pickTitle(raw), options, preselected } };
    },
    render(data, { onComplete, designBrief }) {
        const colorPreferences = designBrief.colorPreferences ?? [];
        const briefFallback = colorPreferences.length
            ? data.options.filter((o) => colorPreferences.includes(o.name)).map((o) => o.value)
            : undefined;
        return (
            <ColorSelector
                options={data.options}
                defaultValues={
                    data.preselected ?? (briefFallback?.length ? briefFallback : undefined)
                }
                onComplete={onComplete}
            />
        );
    },
    completionFor(selectedOptions) {
        return {
            message: {
                type: 'componentMessage',
                role: 'user',
                componentType: 'color-selector',
                data: {
                    value: selectedOptions.map((c) => ({
                        value: c.value,
                        name: c.name,
                        imageUrl: c.imageUrl,
                    })),
                },
            },
            // No briefPatch — backend document response handles this
            skipTurn: false,
        };
    },
    renderMessageView(data: unknown) {
        const d = data as { value: Array<{ value: string; name: string; imageUrl: string }> };
        return (
            <FlexBox gap={3} flexWrap="wrap">
                {d.value.map((color) => (
                    <TileCard
                        key={color.value}
                        imageUrl={color.imageUrl}
                        name={color.name}
                        imageHeight={50}
                        imageObjectFit="fill"
                    />
                ))}
            </FlexBox>
        );
    },
};

const iconTilesDescriptor: SelectorDescriptor<'icon-tiles'> = {
    type: 'icon-tiles',
    // REVIEW_METHOD must run first; this regex matches its substring otherwise.
    marker: /\[COMPONENT:ICON_TILES\]([\s\S]*?)\[\/COMPONENT\]/,
    markerPriority: 40,
    validate(raw) {
        if (!isRecord(raw)) return null;
        const options = mapAll(raw.options, validateTileChoiceOption);
        if (!options) return null;
        const preselected = typeof raw.preselected === 'string' ? raw.preselected : undefined;
        return { type: 'icon-tiles', data: { title: pickTitle(raw), options, preselected } };
    },
    render(data, { onComplete, designBrief }) {
        const briefFallback = data.options.find((o) => o.name === designBrief.designPurpose)?.value;
        return (
            <TileChoiceSelector
                title={data.title || 'Select an option'}
                options={data.options}
                defaultValue={data.preselected ?? briefFallback}
                onComplete={onComplete}
            />
        );
    },
    completionFor(option) {
        return {
            message: {
                type: 'componentMessage',
                role: 'user',
                componentType: 'icon-tiles',
                data: {
                    value: option.value,
                    name: option.name,
                },
            },
            skipTurn: false,
        };
    },
    renderMessageView(data: unknown) {
        const d = data as { value: string; name: string };
        return <TileCard name={d.name} />;
    },
};

const reviewMethodDescriptor: SelectorDescriptor<'review-method'> = {
    type: 'review-method',
    marker: /\[COMPONENT:REVIEW_METHOD\]([\s\S]*?)\[\/COMPONENT\]/,
    markerPriority: 30,
    validate(raw) {
        if (!isRecord(raw)) return null;
        const options = mapAll(raw.options, validateReviewMethodOption);
        if (!options) return null;
        const preselectedMethod = normalizeReviewMethod(raw.preselectedMethod) ?? undefined;
        const preselectedDate =
            typeof raw.preselectedDate === 'string' ? raw.preselectedDate : undefined;
        const preselectedTime =
            typeof raw.preselectedTime === 'string' ? raw.preselectedTime : undefined;
        const data: ReviewMethodData = {
            title: pickTitle(raw),
            options,
            ...(preselectedMethod && { preselectedMethod }),
            ...(preselectedDate && { preselectedDate }),
            ...(preselectedTime && { preselectedTime }),
        };
        return { type: 'review-method', data };
    },
    render(data, { onComplete, designBrief }) {
        return (
            <ReviewMethodSelector
                options={data.options}
                onComplete={onComplete}
                defaultValue={data.preselectedMethod ?? designBrief.requestType}
                preselectedDate={data.preselectedDate}
                preselectedTime={data.preselectedTime}
            />
        );
    },
    completionFor(selection) {
        const briefPatch: Partial<PartialDesignBrief> = {
            requestType: selection.method,
            scheduledDate: selection.scheduledDate,
            scheduledTime: selection.scheduledTime,
            timezone: selection.timezone,
        };

        const confirmation = formatVideoConfirmation(selection);
        const message: Message = confirmation
            ? {
                  type: 'componentMessage',
                  role: 'user',
                  componentType: 'review-method',
                  data: {
                      requestType: 'DesignLive',
                      appointmentTime: confirmation.scheduledTimeUTC,
                  },
              }
            : {
                  type: 'componentMessage',
                  role: 'user',
                  componentType: 'review-method',
                  data: { requestType: 'Standard' },
              };

        return { message, briefPatch };
    },
    renderMessageView(data: unknown) {
        const d = data as {
            requestType?: string;
            appointmentTime?: string;
            date?: string;
            time?: string;
            timezone?: string;
            timezoneKey?: string;
        };

        const isStandard = d.requestType === 'Standard';

        const hasInlineDisplayFields =
            typeof d.date === 'string' &&
            typeof d.time === 'string' &&
            typeof d.timezone === 'string' &&
            typeof d.timezoneKey === 'string';

        const reconstructed =
            !isStandard &&
            !hasInlineDisplayFields &&
            typeof d.appointmentTime === 'string' &&
            typeof d.timezoneKey === 'string'
                ? reconstructVideoConfirmation(d.appointmentTime, d.timezoneKey)
                : null;

        const date = hasInlineDisplayFields ? d.date : reconstructed?.date;
        const time = hasInlineDisplayFields ? d.time : reconstructed?.time;
        const timezone = hasInlineDisplayFields ? d.timezone : reconstructed?.timezone;
        const timezoneKey = hasInlineDisplayFields ? d.timezoneKey : reconstructed?.timezoneKey;

        const isVideoConfirmation =
            !isStandard &&
            typeof date === 'string' &&
            typeof time === 'string' &&
            typeof timezone === 'string' &&
            typeof timezoneKey === 'string';

        const iconType = isStandard ? 'mail' : 'videoChat';
        const title = isStandard ? 'Review online in your own time' : 'Schedule a live video call';

        return (
            <FlexBox
                flexDirection="column"
                gap={4}
                backgroundColor="standard"
                paddingY={isVideoConfirmation ? 5 : 4}
                paddingX={5}
                style={{
                    border: isVideoConfirmation
                        ? undefined
                        : '1px solid var(--swan-sem-color-border-subtle)',
                    borderRadius: isVideoConfirmation
                        ? 'var(--swan-sem-border-radius-strong)'
                        : 'var(--swan-sem-border-radius-standard)',
                    minWidth: isVideoConfirmation ? '280px' : undefined,
                    overflow: 'hidden',
                }}
            >
                <FlexBox alignItems="center" gap={3}>
                    <Icon iconType={iconType} size="standard" />
                    <Typography fontSkin="body-standard-bold">{title}</Typography>
                </FlexBox>
                {isVideoConfirmation && (
                    <FlexBox flexDirection="column" gap={2}>
                        <FlexBox gap={2}>
                            <Typography
                                fontSkin="body-standard"
                                style={{ width: '120px', flexShrink: 0 }}
                            >
                                Date:
                            </Typography>
                            <Typography fontSkin="body-standard">{date}</Typography>
                        </FlexBox>
                        <FlexBox gap={2}>
                            <Typography
                                fontSkin="body-standard"
                                style={{ width: '120px', flexShrink: 0 }}
                            >
                                Session starts:
                            </Typography>
                            <Typography fontSkin="body-standard">{time}</Typography>
                        </FlexBox>
                        <FlexBox gap={2}>
                            <Typography
                                fontSkin="body-standard"
                                style={{ width: '120px', flexShrink: 0 }}
                            >
                                Timezone:
                            </Typography>
                            <Typography fontSkin="body-standard">{timezone}</Typography>
                        </FlexBox>
                    </FlexBox>
                )}
            </FlexBox>
        );
    },
};

const productOptionsDescriptor: SelectorDescriptor<'product-options'> = {
    type: 'product-options',
    marker: /\[COMPONENT:PRODUCT_OPTIONS\]([\s\S]*?)\[\/COMPONENT\]/,
    markerPriority: 60,
    validate(raw) {
        if (!isRecord(raw)) return null;
        const preselectedChoice =
            raw.preselectedChoice === PRODUCT_OPTIONS_CHOICE_CUSTOMER ||
            raw.preselectedChoice === PRODUCT_OPTIONS_CHOICE_DESIGNER
                ? raw.preselectedChoice
                : undefined;
        const preselectedSelections = isStringRecord(raw.preselectedSelections)
            ? raw.preselectedSelections
            : undefined;
        return {
            type: 'product-options',
            data: {
                title: pickTitle(raw),
                mpvId: typeof raw.mpvId === 'string' ? raw.mpvId : '',
                tenant: typeof raw.tenant === 'string' ? raw.tenant : '',
                ...(preselectedChoice && { preselectedChoice }),
                ...(preselectedSelections && { preselectedSelections }),
            },
        };
    },
    render(data, { onComplete }) {
        return <ProductOptionsSelector data={data} onComplete={onComplete} />;
    },
    completionFor(selection) {
        const value: { productOptionsDecision: string; productOptions?: string } = {
            productOptionsDecision: selection.choice,
        };
        if (selection.selections && Object.keys(selection.selections).length > 0) {
            value.productOptions = JSON.stringify(selection.selections);
        }

        return {
            message: {
                type: 'componentMessage',
                role: 'user',
                componentType: 'product-options',
                data: {
                    choice: selection.choice,
                    value,
                    ...(selection.selections && { selections: selection.selections }),
                    ...(selection.selectionDetails && {
                        selectionDetails: selection.selectionDetails,
                    }),
                },
            },
            skipTurn: false,
        };
    },
    renderMessageView(data: unknown) {
        const d = data as {
            choice: string;
            selections?: Record<string, string>;
            selectionDetails?: Array<{ optionName: string; valueName: string; imageUrl?: string }>;
        };
        if (d.choice === PRODUCT_OPTIONS_CHOICE_DESIGNER) {
            return (
                <ConfirmationCard>
                    <FlexBox alignItems="center" gap={3} paddingY={4} paddingX={5}>
                        <Icon iconType="workWithAPro" size="standard" />
                        <Typography fontSkin="body-standard-bold">
                            Let my designer decide
                        </Typography>
                    </FlexBox>
                </ConfirmationCard>
            );
        }

        const details: Array<{ optionName: string; valueName: string; imageUrl?: string }> =
            d.selectionDetails ??
            (d.selections
                ? Object.entries(d.selections).map(([key, value]) => ({
                      optionName: key,
                      valueName: value,
                  }))
                : []);

        return (
            <ConfirmationCard>
                <FlexBox flexDirection="column" gap={3} paddingY={4} paddingX={5}>
                    <FlexBox alignItems="center" gap={3}>
                        <Icon iconType="printedProducts" size="standard" />
                        <Typography fontSkin="body-standard-bold">
                            I know my product specs
                        </Typography>
                    </FlexBox>
                    {details.map((detail) => (
                        <FlexBox key={detail.optionName} alignItems="center" gap={3}>
                            {detail.imageUrl && (
                                <img
                                    src={detail.imageUrl}
                                    alt={detail.valueName}
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '4px',
                                        objectFit: 'cover',
                                    }}
                                />
                            )}
                            <Typography fontSkin="body-standard">
                                <Typography fontSkin="body-standard-bold" as="span">
                                    {detail.optionName}:
                                </Typography>{' '}
                                {detail.valueName}
                            </Typography>
                        </FlexBox>
                    ))}
                </FlexBox>
            </ConfirmationCard>
        );
    },
};

const referenceProjectsDescriptor: SelectorDescriptor<'reference-projects'> = {
    type: 'reference-projects',
    marker: /\[COMPONENT:REFERENCE_PROJECTS\]([\s\S]*?)\[\/COMPONENT\]/,
    markerPriority: 50,
    validate(raw) {
        if (!isRecord(raw)) return null;
        const preselectedDecision =
            raw.preselectedDecision === REFERENCE_PROJECTS_DECISION_WITH ||
            raw.preselectedDecision === REFERENCE_PROJECTS_DECISION_WITHOUT
                ? (raw.preselectedDecision as ReferenceProjectsData['preselectedDecision'])
                : undefined;
        const preselectedWorkIds =
            Array.isArray(raw.preselectedWorkIds) &&
            raw.preselectedWorkIds.every((id) => typeof id === 'string')
                ? (raw.preselectedWorkIds as string[])
                : undefined;
        return {
            type: 'reference-projects',
            data: {
                title: pickTitle(raw),
                ...(preselectedDecision && { preselectedDecision }),
                ...(preselectedWorkIds && { preselectedWorkIds }),
            },
        };
    },
    render(data, { onComplete }) {
        return <ReferenceProjectsSelector data={data} onComplete={onComplete} />;
    },
    completionFor(result) {
        if (result.decision === REFERENCE_PROJECTS_DECISION_WITHOUT) {
            return {
                message: {
                    type: 'componentMessage',
                    role: 'user',
                    componentType: 'reference-projects',
                    data: {
                        decision: REFERENCE_PROJECTS_DECISION_WITHOUT,
                        value: {
                            referenceProjectsDecision: REFERENCE_PROJECTS_DECISION_WITHOUT,
                        },
                    },
                },
                skipTurn: false,
            };
        }
        return {
            message: {
                type: 'componentMessage',
                role: 'user',
                componentType: 'reference-projects',
                data: {
                    decision: REFERENCE_PROJECTS_DECISION_WITH,
                    workIds: result.workIds,
                    value: {
                        referenceProjectsDecision: REFERENCE_PROJECTS_DECISION_WITH,
                        referenceProjects: result.workIds.join(','),
                    },
                },
            },
            skipTurn: false,
        };
    },
    renderMessageView(data: unknown) {
        const d = data as {
            decision?: string;
            workIds?: string[];
            value?: Record<string, unknown>;
        };

        if (d.decision === REFERENCE_PROJECTS_DECISION_WITHOUT) {
            return (
                <ConfirmationCard>
                    <FlexBox alignItems="center" gap={3} paddingY={4} paddingX={5}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                        >
                            <path
                                stroke="currentColor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="m9.5 10.5 5 5m0-5-5 5M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2z"
                            />
                        </svg>
                        <Typography fontSkin="body-standard-bold">No, continue without</Typography>
                    </FlexBox>
                </ConfirmationCard>
            );
        }

        // "with" decision - show icon + text + project thumbnails
        const workIds = d.workIds ?? [];
        return (
            <ConfirmationCard>
                <FlexBox flexDirection="column" gap={3} paddingY={4} paddingX={5}>
                    <FlexBox alignItems="center" gap={3}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                        >
                            <path
                                stroke="currentColor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="m6 14 1.5-2.9A2 2 0 0 1 9.24 10H20m0 0a2 2 0 0 1 1.94 2.5l-1.54 6a2 2 0 0 1-1.95 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2z"
                            />
                        </svg>
                        <Typography fontSkin="body-standard-bold">Yes, select projects</Typography>
                    </FlexBox>
                    {workIds.length > 0 && (
                        <Typography fontSkin="body-small">
                            {workIds.length} project{workIds.length !== 1 ? 's' : ''} selected
                        </Typography>
                    )}
                </FlexBox>
            </ConfirmationCard>
        );
    },
};

/* ─── Registry ──────────────────────────────────────────────────────────── */

export const SELECTOR_REGISTRY = {
    'style-tiles': styleTilesDescriptor,
    'color-selector': colorSelectorDescriptor,
    'icon-tiles': iconTilesDescriptor,
    'review-method': reviewMethodDescriptor,
    'product-options': productOptionsDescriptor,
    'reference-projects': referenceProjectsDescriptor,
} as const satisfies { [K in SelectorType]: SelectorDescriptor<K> };

/** Descriptors ordered by markerPriority for the parseClaudeResponse scanner. */
export const SELECTOR_DESCRIPTORS_BY_PRIORITY: ReadonlyArray<SelectorDescriptor<SelectorType>> = (
    Object.values(SELECTOR_REGISTRY) as SelectorDescriptor<SelectorType>[]
)
    .slice()
    .sort((a, b) => a.markerPriority - b.markerPriority);

/** Typed lookup that preserves the descriptor's generic. */
export function getSelector<T extends SelectorType>(type: T): SelectorDescriptor<T> {
    return SELECTOR_REGISTRY[type] as SelectorDescriptor<T>;
}

/**
 * Look up the message-view renderer for a given component type string.
 * Returns undefined if the type is unknown or the descriptor hasn't implemented renderMessageView.
 *
 * NOTE: Uses SELECTOR_REGISTRY indexed as Record<string, ...> because the `type` parameter
 * is an arbitrary string (from parsed JSON), not a validated SelectorType enum value.
 */
export function getMessageViewRenderer(type: string): ((data: unknown) => ReactNode) | undefined {
    const descriptor = (SELECTOR_REGISTRY as Record<string, SelectorDescriptor<SelectorType>>)[
        type
    ];
    return descriptor?.renderMessageView;
}
